import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  BookOpen,
  CheckCircle,
  Clock,
  FileEdit
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminArticle {
  id: number
  slug: string
  title: string
  excerpt: string | null
  author_name: string
  category_name: string | null
  community_name: string | null
  views_count: number
  published: boolean
  published_at: string | null
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  community_id: string | null
  category_id: string | null
  status: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface Category {
  id: number
  name: string
  community_id: number
  parent_id: number | null
}

interface IndexProps {
  articles: AdminArticle[]
  pagination: Pagination
  filters: Filters
  communities: Community[]
  categories: Category[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getStatusBadge(article: AdminArticle): { label: string; icon: typeof CheckCircle; className: string } {
  if (article.published) {
    return {
      label: 'Publie',
      icon: CheckCircle,
      className: 'bg-green-100 text-green-800'
    }
  }
  if (article.published_at) {
    return {
      label: 'Programme',
      icon: Clock,
      className: 'bg-blue-100 text-blue-800'
    }
  }
  return {
    label: 'Brouillon',
    icon: FileEdit,
    className: 'bg-gray-100 text-gray-600'
  }
}

export default function Index({ articles, pagination, filters, communities, categories }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  // Filter categories by community
  const communityCategories = filters.community_id
    ? categories.filter(c => c.community_id === parseInt(filters.community_id!))
    : categories

  // Group categories hierarchically: root categories first, then their children
  const hierarchicalCategories = communityCategories
    .filter(c => c.parent_id === null)
    .flatMap(parent => [
      parent,
      ...communityCategories.filter(c => c.parent_id === parent.id)
    ])

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/articles', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/articles', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((article: AdminArticle) => {
    if (confirm(`Voulez-vous vraiment supprimer l'article "${article.title}" ?`)) {
      router.delete(`/admin/articles/${article.slug}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Articles"
      breadcrumbs={[{ label: 'Articles' }]}
    >
      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par titre, extrait..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/articles/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouvel article
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null, category_id: null })}
            >
              <option value="">Toutes communautes</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.category_id || ''}
              onChange={(e) => applyFilters({ category_id: e.target.value || null })}
            >
              <option value="">Toutes categories</option>
              {hierarchicalCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.parent_id ? `— ${category.name}` : category.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-40">
            <Select
              value={filters.status || ''}
              onChange={(e) => applyFilters({ status: e.target.value || null })}
            >
              <option value="">Tous statuts</option>
              <option value="published">Publies</option>
              <option value="draft">Brouillons</option>
              <option value="scheduled">Programmes</option>
            </Select>
          </div>

          {(filters.search || filters.community_id || filters.category_id || filters.status) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  community_id: null,
                  category_id: null,
                  status: null
                })
              }}
            >
              Reinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} article{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Articles Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('title')}
                  >
                    Article
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Categorie
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Auteur
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Statut
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('views_count')}
                  >
                    <Eye size={14} />
                    Vues
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('published_at')}
                  >
                    Publication
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {articles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <BookOpen size={32} className="text-gray-300" />
                      Aucun article trouve
                    </div>
                  </td>
                </tr>
              ) : (
                articles.map((article) => {
                  const statusBadge = getStatusBadge(article)
                  const StatusIcon = statusBadge.icon
                  return (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="max-w-xs">
                          <p className="font-medium text-text-dark truncate">{article.title}</p>
                          {article.excerpt && (
                            <p className="text-xs text-text-gray truncate mt-0.5">{article.excerpt}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark">
                          {article.category_name || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark">
                          {article.author_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${statusBadge.className}`}>
                          <StatusIcon size={12} />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark font-medium">
                          {article.views_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-gray">
                        {article.published_at ? formatDate(article.published_at) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link href={`/admin/articles/${article.slug}`}>
                            <Button variant="secondary" className="p-2">
                              <Eye size={16} />
                            </Button>
                          </Link>
                          <Link href={`/admin/articles/${article.slug}/edit`}>
                            <Button variant="secondary" className="p-2">
                              <Edit size={16} />
                            </Button>
                          </Link>
                          <Button
                            variant="secondary"
                            className="p-2 text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(article)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Precedent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
